var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { Trans, useTranslation } from "react-i18next";
import { message, Spin, Descriptions, Button } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import { Permissions } from "@whatsper/texterchat-common";
import { useStore } from "../../../../store/rootStore";
import { Operation } from "../../../EntityManager";
import { ManagerDetailsView } from "../core";
import NewAuthorization from "./components/NewAuthorization";
import { SERVICE_ADDITIONAL_QUERY, SERVICES_REDIRECT_ONLY } from "./components/servicesCustom";
import { getAxios } from "../../../../backend";
import { API_PATH, SERVER_URL } from "../../../../constants";
import styles from "./OAuthDetailsView.module.scss";
const datetimeFormat = "Do MMM YY, ddd, h:mm a";
const OAuthDetailsView = observer(({ entityId, operation, onCancel, onCompleted, }) => {
    const { agentsStore } = useStore();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [credentials, setCredentials] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    // Reload data when entityId changes
    useEffect(() => {
        setDataLoaded(false);
        setShowDeleteConfirm(false);
        loadData();
    }, [entityId]);
    const loadData = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const axios = yield getAxios();
            const { data } = yield axios.get(`${API_PATH.AUTH}/oauth/credentials`);
            setCredentials(data);
            setDataLoaded(true);
        }
        catch (error) {
            console.error("Failed to load data:", error);
            setDataLoaded(true);
        }
    });
    const entity = (() => {
        if (!entityId || operation === Operation.CREATE)
            return null;
        return credentials.find((c) => c._id === entityId) || null;
    })();
    if (entityId && operation !== Operation.CREATE && !dataLoaded) {
        return (React.createElement("div", { style: { display: "flex", justifyContent: "center", alignItems: "center", height: "200px" } },
            React.createElement(Spin, { size: "large" })));
    }
    const renderDeleteQuestion = (cred) => {
        return React.createElement(Trans, { i18nKey: "sureDeleteOAuthCred", values: { service: cred.service } });
    };
    const handleSave = (formData) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            const { service, scope, usage } = formData;
            const query = new URLSearchParams();
            scope.forEach((name, i) => {
                query.set(`scope[${i}]`, name);
            });
            if (typeof SERVICE_ADDITIONAL_QUERY[service.name] === "object") {
                for (const key in SERVICE_ADDITIONAL_QUERY[service.name]) {
                    query.set(key, SERVICE_ADDITIONAL_QUERY[service.name][key]);
                }
            }
            const url = `${SERVER_URL}${API_PATH.AUTH}/oauth/v2/authorize/${service.name}/${usage}?${query.toString()}`;
            if (SERVICES_REDIRECT_ONLY.includes(service.name)) {
                window.location.assign(url);
                return;
            }
            const width = 500;
            const height = 700;
            const left = Math.round((window.innerWidth - width) / 2);
            const top = Math.round((window.innerHeight - height) / 2);
            window.open(url, "_blank", `width=${width},height=${height},left=${left > 0 ? left : 0},top=${top > 0 ? top : 0},popup,status,scrollbars`);
            // Close the drawer after opening the popup
            onCompleted();
        }
        catch (error) {
            console.error("Failed to start OAuth:", error);
            message.error(React.createElement(Trans, { i18nKey: "oauthError" }));
            throw error;
        }
        finally {
            setLoading(false);
        }
    });
    const handleDelete = (cred) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            const axios = yield getAxios();
            yield axios.delete(`${API_PATH.AUTH}/oauth/credentials/${cred._id}`);
            message.success(React.createElement(Trans, { i18nKey: "oauthDeleteSuccess", values: { service: cred.service } }));
            const { data } = yield axios.get(`${API_PATH.AUTH}/oauth/credentials`);
            setCredentials(data);
            onCompleted();
        }
        catch (error) {
            console.error("Failed to delete OAuth credential:", error);
            message.error(React.createElement(Trans, { i18nKey: "oauthDeleteError" }));
            throw error;
        }
        finally {
            setLoading(false);
        }
    });
    const canDelete = agentsStore.hasPermissions([Permissions.Permission.oauth_manage_all]) ||
        agentsStore.hasPermissions([Permissions.Permission.oauth_manage_own]) ||
        agentsStore.hasPermissions([Permissions.Permission.oauth_manage_default]);
    const getAgentName = (userId) => {
        const userInfo = agentsStore.getAgentData(userId);
        return userInfo ? userInfo.displayName || userInfo.email || userInfo._id : userId;
    };
    if ((operation === Operation.EDIT || operation === Operation.DELETE) && entity && !showDeleteConfirm) {
        return (React.createElement("div", { className: styles.detailsContainer },
            React.createElement("h3", { className: styles.title }, entity.service),
            React.createElement(Descriptions, { bordered: true, size: "small", column: 1, className: styles.descriptions },
                React.createElement(Descriptions.Item, { label: React.createElement(Trans, { i18nKey: "createdAt" }) }, moment.unix(entity.created).format(datetimeFormat)),
                entity.updated && (React.createElement(Descriptions.Item, { label: React.createElement(Trans, { i18nKey: "updatedAt" }) }, moment.unix(entity.updated).format(datetimeFormat))),
                React.createElement(Descriptions.Item, { label: React.createElement(Trans, { i18nKey: "usage" }) }, entity.user
                    ? t("oauthTokenUsingForUser", { agent: getAgentName(entity.user) })
                    : t("oauthTokenUsageApp"))),
            React.createElement("div", { className: styles.actions },
                canDelete && (React.createElement(Button, { danger: true, icon: React.createElement(DeleteOutlined, null), onClick: () => setShowDeleteConfirm(true), loading: loading },
                    React.createElement(Trans, { i18nKey: "delete" }))),
                React.createElement(Button, { onClick: onCancel },
                    React.createElement(Trans, { i18nKey: "close" })))));
    }
    if ((operation === Operation.EDIT || operation === Operation.DELETE) && entity && showDeleteConfirm) {
        return (React.createElement("div", { className: styles.detailsContainer },
            React.createElement("div", { className: styles.deleteConfirm },
                React.createElement("p", null, renderDeleteQuestion(entity)),
                React.createElement("div", { className: styles.actions },
                    React.createElement(Button, { danger: true, onClick: () => handleDelete(entity), loading: loading },
                        React.createElement(Trans, { i18nKey: "delete" })),
                    React.createElement(Button, { onClick: () => setShowDeleteConfirm(false) },
                        React.createElement(Trans, { i18nKey: "cancel" }))))));
    }
    return (React.createElement(ManagerDetailsView, { entity: entity, entityId: entityId, operation: operation, form: NewAuthorization, formProps: {}, loading: loading, renderDeleteQuestion: renderDeleteQuestion, onEntitySave: handleSave, onEntityDelete: canDelete ? handleDelete : undefined, onCancel: onCancel, onSaveCompleted: onCompleted, onDeleteCompleted: onCompleted }));
});
export default OAuthDetailsView;
